import { usePeopleManagementApi } from 'infrastructure/api';
import { useCacheService } from 'framework/cache';
import moment from 'moment';
import { LEAVE_STATUSES_ALIASES, PAID_LEAVE_SUB_TYPES_ALIAS } from 'domain/shared/leave';
import { isPolishOfficeCode, OFFICE_NAMES } from 'domain/shared/employee';
import { LeaveBalancesApiResponse, MappedLeaveItem, Period, ProcessedLeaveYear, AccrualPeriodLeave } from './types';
import {
  calculateTaken,
  getPolishContractorDisplayMode,
  getPreparedOtherLeaves,
  isExtended,
  sortLeavesYears,
} from './leaveBalanceUtils';
import { DAY_ON_DEMAND_TOTAL_BALANCE, EMPLOYEE_CONTRACT_TYPE_ALIASES, LEAVE_BALANCE_CACHE_NAME } from './constants';
import { LoadingParams } from 'framework/list';

const getCacheId = (employeeAlias: string) => `employee-${employeeAlias}-leave-balance`;
const requestPromises: { [key: string]: Promise<LeaveBalancesApiResponse> | undefined } = {};

const DAY_ON_DEMAND_EXCLUDED_STATUSES = new Set([LEAVE_STATUSES_ALIASES.REJECTED]);

const toNumber = (value?: number | null) => value ?? 0;

const hasDayOnDemandBalance = (office: string, contractType: string) =>
  isPolishOfficeCode(office) && contractType === EMPLOYEE_CONTRACT_TYPE_ALIASES.EC;

const getLeaveStartDate = (leave: MappedLeaveItem) => leave.startDate || leave.period?.startDate || '';
const getPeriodKey = (startDate: string, endDate: string) => `${startDate}_${endDate}`;

const withLeaveUiFields = (leave: AccrualPeriodLeave): MappedLeaveItem => ({
  ...leave,
  startDate: leave.period.startDate,
  endDate: leave.period.plannedEndDate,
  actualEndDate: leave.period.actualEndDate,
  duration: leave.period.duration,
  isExtended: isExtended(leave.period.actualEndDate, leave.period.plannedEndDate),
});

const getDayOnDemandRemainedBalance = (leaves: AccrualPeriodLeave[], paidLeavesRemainedBalance?: number | null) => {
  const cappedPaidLeavesRemainedBalance = Math.max(toNumber(paidLeavesRemainedBalance), 0);
  const takenDays = leaves
    .filter(
      leave =>
        leave.subType === PAID_LEAVE_SUB_TYPES_ALIAS.DAY_ON_DEMAND &&
        !DAY_ON_DEMAND_EXCLUDED_STATUSES.has(leave.status),
    )
    .reduce((total, leave) => total + leave.takenFromPeriod, 0);

  const remainedDayOnDemandBalance = Math.max(DAY_ON_DEMAND_TOTAL_BALANCE - takenDays, 0);

  return Math.min(remainedDayOnDemandBalance, cappedPaidLeavesRemainedBalance);
};

const useLeaveBalanceService = () => {
  const { sendGetRequest } = usePeopleManagementApi();
  const { getValue, addToCache } = useCacheService<LeaveBalancesApiResponse>(LEAVE_BALANCE_CACHE_NAME);

  const fetchRawData = async (employeeAlias: string): Promise<LeaveBalancesApiResponse> => {
    const cacheId = getCacheId(employeeAlias);
    const cachedValue = getValue(cacheId);

    if (cachedValue) {
      return cachedValue;
    }

    if (requestPromises[cacheId]) {
      return requestPromises[cacheId];
    }

    requestPromises[cacheId] = (async () => {
      try {
        const response = await sendGetRequest(`/employees/${employeeAlias}/leaves/balances`);
        const responseData = (await response.json()) as LeaveBalancesApiResponse;

        addToCache(cacheId, responseData);

        return responseData;
      } finally {
        delete requestPromises[cacheId];
      }
    })();

    return requestPromises[cacheId];
  };

  const getPaidLeavesBalances = async (employeeAlias: string): Promise<ProcessedLeaveYear[]> => {
    const data = await fetchRawData(employeeAlias);

    const rows = Object.values(data).flatMap(officeData => {
      const paidLeaveBalance = officeData.leaveTypeBalances.PAID_LEAVE;

      if (!paidLeaveBalance) {
        return [];
      }

      const paidPeriodsByKey = new Map(
        (paidLeaveBalance?.accrualPeriods || []).map(period => [
          getPeriodKey(period.startDate, period.endDate),
          period,
        ]),
      );
      const periodKeys = Array.from(paidPeriodsByKey.keys());

      return periodKeys.flatMap(periodKey => {
        const paidPeriod = paidPeriodsByKey.get(periodKey) || null;

        if (!paidPeriod) {
          return [];
        }

        const period = paidPeriod;

        const hasDayOnDemand =
          !!paidPeriod && hasDayOnDemandBalance(period.contractInfo.office, period.contractInfo.contractType);
        const leaves = [...(paidPeriod?.relatedLeaves || [])]
          .map(withLeaveUiFields)
          .sort(
            (a, b) =>
              new Date((b.startDate as string) || '').getTime() - new Date((a.startDate as string) || '').getTime(),
          );
        const { isPolishContractor, hideBalanceColumns, skipPeriod } = getPolishContractorDisplayMode(
          period.contractInfo,
          paidPeriod?.balanceInfo || null,
          paidPeriod?.relatedLeaves || [],
        );

        if (skipPeriod) {
          return [];
        }

        return [
          {
            totalRemainedBalance: String(toNumber(paidLeaveBalance?.totalRemainedBalance)),
            startDate: period.startDate,
            endDate: period.endDate,
            officeName: OFFICE_NAMES[period.contractInfo.office] || period.contractInfo.office,
            officeCode: period.contractInfo.office,
            contractType: period.contractInfo.contractType,
            isPolishContractor,
            hideBalanceColumns,
            hasDayOnDemandBalance: hasDayOnDemand,
            paidLeavesRemainedBalance: String(toNumber(paidPeriod?.balanceInfo?.remainedBalance)),
            paidLeavesTotalBalance: String(toNumber(paidPeriod?.balanceInfo?.accruedBalance)),
            paidLeavesExpiredBalance: String(toNumber(paidPeriod?.balanceInfo?.expiredBalance)),
            taken: calculateTaken(paidPeriod?.balanceInfo?.accruedBalance, paidPeriod?.balanceInfo?.remainedBalance),
            leaves,
            ...(hasDayOnDemand
              ? {
                  dayOnDemandsTotalBalance: String(DAY_ON_DEMAND_TOTAL_BALANCE),
                  dayOnDemandsRemainedBalance: String(
                    getDayOnDemandRemainedBalance(
                      paidPeriod?.relatedLeaves || [],
                      paidPeriod?.balanceInfo?.remainedBalance,
                    ),
                  ),
                }
              : {}),
          },
        ];
      });
    });

    return sortLeavesYears(rows);
  };

  const getSickDaysBalances = async (employeeAlias: string): Promise<ProcessedLeaveYear[]> => {
    const data = await fetchRawData(employeeAlias);

    const rows = Object.values(data).flatMap(officeData => {
      const sickDayBalance = officeData.leaveTypeBalances.SICK_DAY;
      if (!sickDayBalance) {
        return [];
      }

      return sickDayBalance.accrualPeriods.flatMap(period => {
        const { isPolishContractor, hideBalanceColumns, skipPeriod } = getPolishContractorDisplayMode(
          period.contractInfo,
          period.balanceInfo,
          period.relatedLeaves,
        );

        if (skipPeriod) {
          return [];
        }

        return [
          {
            startDate: period.startDate,
            endDate: period.endDate,
            officeName: OFFICE_NAMES[period.contractInfo.office] || period.contractInfo.office,
            officeCode: period.contractInfo.office,
            contractType: period.contractInfo.contractType,
            isPolishContractor,
            hideBalanceColumns,
            leaves: period.relatedLeaves
              .map(withLeaveUiFields)
              .sort(
                (a, b) =>
                  new Date((b.startDate as string) || '').getTime() - new Date((a.startDate as string) || '').getTime(),
              ),
            sickDaysRemainedBalance: String(toNumber(period.balanceInfo?.remainedBalance)),
            sickDaysTotalBalance: String(toNumber(period.balanceInfo?.accruedBalance)),
            sickDaysExpiredBalance: String(toNumber(period.balanceInfo?.expiredBalance)),
            taken: period.balanceInfo
              ? calculateTaken(period.balanceInfo?.accruedBalance, period.balanceInfo?.remainedBalance)
              : 0,
          },
        ];
      });
    });

    return sortLeavesYears(rows);
  };

  const getOtherLeavesBalances = async (employeeAlias: string) => {
    const result = await fetchRawData(employeeAlias);
    return getPreparedOtherLeaves(result);
  };

  const getPeriods = async (employeeAlias: string) => {
    const data = await fetchRawData(employeeAlias);
    const allPeriods = new Set<string>();

    Object.values(data).forEach(officeData => {
      Object.values(officeData.leaveTypeBalances).forEach(typeBalance => {
        typeBalance.accrualPeriods.forEach(period => {
          allPeriods.add(`${period.startDate}_${period.endDate}`);
        });
      });
    });

    return Array.from(allPeriods)
      .map(periodKey => {
        const [startDate, endDate] = periodKey.split('_');
        return {
          value: periodKey,
          label: `${startDate} - ${endDate}`,
          startDate,
          endDate,
        };
      })
      .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
  };

  const getCurrentPeriod = (periods: Period[]): Period | null => {
    const now = moment();
    const found = periods.find(period => {
      return moment(now).isSameOrAfter(period.startDate, 'day') && moment(now).isSameOrBefore(period.endDate, 'day');
    });

    return found || null;
  };

  const filterByCurrentAndNextPeriod = (
    items: ProcessedLeaveYear[],
    currentPeriod: Period | null,
    nextPeriod: Period | null,
    showAllPeriods: boolean,
  ) => {
    if (showAllPeriods) {
      return items;
    }

    if (!currentPeriod) {
      return [];
    }

    return items.filter(item => {
      const itemStartDate = new Date(item.startDate);
      const itemEndDate = new Date(item.endDate);
      const currentStart = new Date(currentPeriod.startDate);
      const currentEnd = new Date(currentPeriod.endDate);
      const isCurrentPeriod =
        itemStartDate.getTime() === currentStart.getTime() && itemEndDate.getTime() === currentEnd.getTime();

      if (!nextPeriod) {
        return isCurrentPeriod;
      }

      const nextStart = new Date(nextPeriod.startDate);
      const nextEnd = new Date(nextPeriod.endDate);
      const isNextPeriod =
        itemStartDate.getTime() === nextStart.getTime() && itemEndDate.getTime() === nextEnd.getTime();

      return isCurrentPeriod || (isNextPeriod && item.leaves.length > 0);
    });
  };

  const getFilteredPaidLeaves = (
    paidLeaves: ProcessedLeaveYear[],
    currentPeriod: Period | null,
    nextPeriod: Period | null,
    showAllPeriods: boolean,
  ) => filterByCurrentAndNextPeriod(paidLeaves, currentPeriod, nextPeriod, showAllPeriods);

  const getFilteredSickDays = (
    sickDays: ProcessedLeaveYear[],
    currentPeriod: Period | null,
    nextPeriod: Period | null,
    showAllPeriods: boolean,
  ) => filterByCurrentAndNextPeriod(sickDays, currentPeriod, nextPeriod, showAllPeriods);

  const getFilteredOtherLeaves = (otherLeaves: MappedLeaveItem[], loadParams: Partial<LoadingParams>) => {
    const startDate = loadParams.filter?.startDate?.ge;
    const endDate = loadParams.filter?.startDate?.le;

    if (!startDate && !endDate) {
      return otherLeaves;
    }

    return otherLeaves.filter(leave => {
      const leaveStartDate = new Date(getLeaveStartDate(leave));

      if (startDate && endDate) {
        return leaveStartDate >= new Date(startDate) && leaveStartDate <= new Date(endDate);
      }

      if (startDate) {
        return leaveStartDate >= new Date(startDate);
      }

      if (endDate) {
        return leaveStartDate <= new Date(endDate);
      }

      return true;
    });
  };

  const getNextPeriod = (periods: Period[], current: Period): Period | null => {
    const currentEndDate = new Date(current.endDate);

    return periods.find(period => new Date(period.startDate) > currentEndDate) || null;
  };

  return {
    getPaidLeavesBalances,
    getSickDaysBalances,
    getOtherLeavesBalances,
    getPeriods,
    getCurrentPeriod,
    getNextPeriod,
    getFilteredPaidLeaves,
    getFilteredSickDays,
    getFilteredOtherLeaves,
  };
};

export const clearCache = () => {
  Object.keys(requestPromises).forEach(key => delete requestPromises[key]);
};

export default useLeaveBalanceService;
